import { Button } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import React from 'react';

import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { EAdserverCreativeType } from '~/src/dash/api/console-api';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Text } from '~/src/dash/publicaUI';
import { isPBSDomain } from '~/src/dash/resellers/utils/utils';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  IAdserverCampaignWithSelector,
  IAdserverCreativeWithCampaignInfoAndSelector,
} from '~/src/dash/views/creatives/creative-library-list-view';

export const ValidBannerExtensions: string[] = ['jpg', 'jpeg', 'png', 'gif'];
export const ValidVideoExtensions: string[] = ['mp4', 'mov'];
export const ValidBannerMimetypes: string[] = ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'];
export const ValidVideoMimetypes: string[] = ['video/mp4', 'video/mov'];
export const ValidBannerDimensions: string[] = [
  '300,250',
  '1840,230',
  '1629,420',
  '970,250',
  '1440,1080',
  '1920,1080',
];

export const CreateBannerFromAssetURLRow = (handleOpenCreateBannerModal: () => void) => {
  return (
    <Button
      component='label'
      onClick={() => {
        handleOpenCreateBannerModal();
      }}
    >
      <Cols
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minWidth: '128px',
          maxHeight: '16px',
          marginLeft: '-24px',
        }}
      >
        <Col horizontalPadding>
          <LinkIcon
            style={{
              color: `${publicaColors.blue}`,
            }}
          />
        </Col>
        <Col>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: FONT_WEIGHT.bold,
              marginBottom: '5px',
              color: publicaColors.blue,
            }}
          >
            ADD BANNER BY URL
          </Text>
        </Col>
      </Cols>
    </Button>
  );
};

export const CreateBannerFromDirectUploadRow = (
  handleDirectUploadBanner: (
    { target }: React.ChangeEvent<HTMLInputElement>,
    isBanner?: boolean,
  ) => void,
) => {
  return (
    <Button component='label'>
      <Cols
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minWidth: '128px',
          maxHeight: '16px',
          marginLeft: '-24px',
        }}
      >
        <Col horizontalPadding>
          <PhotoLibraryIcon
            style={{
              color: `${publicaColors.blue}`,
            }}
          />
        </Col>
        <Col>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: FONT_WEIGHT.bold,
              marginBottom: '5px',
              color: publicaColors.blue,
            }}
          >
            DIRECT UPLOAD BANNER
          </Text>
        </Col>
      </Cols>
      <input
        type='file'
        style={{ display: 'none' }}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
          handleDirectUploadBanner(evt, true);
        }}
      />
    </Button>
  );
};

export const isValidBannerURL = (url: string): boolean => {
  return ValidBannerExtensions.some((ext) => url.endsWith('.' + ext));
};

export const isValidBannerExtension = (ext: string, withPrefixedPeriod?: boolean): boolean => {
  if (!ext) {
    return false;
  } else if (withPrefixedPeriod) {
    return ValidBannerExtensions.includes(ext.slice(1));
  }
  return ValidBannerExtensions.includes(ext);
};

export const isValidVideoURL = (url: string): boolean => {
  return ValidVideoExtensions.some((ext) => url.endsWith(ext));
};

export const isValidVideoExtension = (ext: string, withPrefixedPeriod?: boolean): boolean => {
  if (!ext) {
    return false;
  } else if (withPrefixedPeriod) {
    return ValidVideoExtensions.includes(ext.slice(1));
  }
  return ValidVideoExtensions.includes(ext);
};

export const isValidVideoMimetype = (mimeType: string): boolean => {
  return ValidVideoMimetypes.includes(mimeType);
};

export const isValidBannerMimetype = (mimeType: string): boolean => {
  return ValidBannerMimetypes.includes(mimeType);
};

type RejectBannerUploadType = {
  rejected: boolean;
  reason: string;
};

export const shouldRejectBannerUpload = (name: string, url: string): RejectBannerUploadType => {
  const validationResult: RejectBannerUploadType = { rejected: false, reason: '' };
  if (name.length === 0) {
    validationResult.rejected = true;
    validationResult.reason = 'Banner asset must have a name.';
  } else if (url.length === 0) {
    validationResult.rejected = true;
    validationResult.reason = 'Banner asset must have a URL.';
  } else if (isPBSDomain(url)) {
    validationResult.rejected = true;
    validationResult.reason = 'Cannot target a domain with that root.';
  } else if (!isValidBannerURL(url)) {
    validationResult.rejected = true;
    validationResult.reason = 'Currently only .png, .jpg, and .gif image formats are supported';
  }
  return validationResult;
};

/**
 * isPrimaryBannerForLineItemWithSecondaryBanner checks if a selected mediafile is a primary banner on a specified line item that has two banners. The intention is to disallow removing the primary banner, otherwise the secondary banner has a side effect of being promoted to primary.
 * @param selected is the selected mediafile
 * @param lineItem is the selected line item, which contains multiple creatives, some of which might be banners.
 * @returns whether the line item has two banners and the selected global mediafile is the primary one
 */
export const isPrimaryBannerForLineItemWithSecondaryBanner = (
  selected: IAdserverCreativeWithCampaignInfoAndSelector,
  lineItem: IAdserverCampaign,
): boolean => {
  const bannerCreatives = lineItem.AdserverCreatives.filter(
    (ad) => ad?.CreativeType === EAdserverCreativeType.Banner,
  );
  if (bannerCreatives.length < 2) {
    return false;
  }
  const files = bannerCreatives[0]?.AdserverGlobalMediafiles;
  if (
    files !== undefined &&
    files.some((file) => selected.AdserverGlobalMediafileIDs.includes(file.ID))
  ) {
    return true;
  }
  return false;
};

export const hasPrimaryAndSecondaryBanner = (lineItem: IAdserverCampaignWithSelector): boolean => {
  return (
    lineItem.AdserverCreatives.filter((creative) => {
      return creative?.CreativeType === EAdserverCreativeType.Banner;
    }).length >= 2
  );
};
